import Axios, { AxiosRequestConfig } from 'axios';
import { FileType } from 'components/Dropzone/utils';
import { PresignedFile, UploadedFileResponse } from 'models/File';
import {
  FILE_EXTENSIONS,
  FILE_TYPES,
} from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import { ObjectValues } from 'types';

export const BATCH_SIZE = 3;

export const DUPLICATE_STATUSES = {
  SKIP: 'skip',
  KEEP: 'keep',
  REPLACE: 'replace',
} as const;

export type DuplicateStatuses = ObjectValues<typeof DUPLICATE_STATUSES>;
export type EnhancedFile = File & {
  path?: string;
} & DuplicateNode;

export type UseDuplicatesReturn = {
  nodeId?: string;
  suggestedName?: string;
};

export type DuplicateNode = {
  duplicateNodeId?: string;
  duplicateStatus?: DuplicateStatuses;
};

export const getFilePath = ({ path, webkitRelativePath }: EnhancedFile) =>
  path || webkitRelativePath;

export const getMimeTypeByNameExtension = (fileName: string) => {
  if (fileName.toLowerCase().endsWith(FILE_EXTENSIONS.DWG))
    return FILE_TYPES.DWG;

  if (fileName.toLowerCase().endsWith(FILE_EXTENSIONS.TIF))
    return FILE_TYPES.IMAGE_TIFF;

  return undefined;
};

export const getFileParams = (
  fileName: string,
  file: File,
  customFileName?: string,
) => {
  const path = getFilePath(file as EnhancedFile);

  return {
    fileName,
    path,
    originalname: file.name,
    mimetype: file.type || getMimeTypeByNameExtension(file.name),
    fileSize: file.size,
    ...(customFileName ? { customFileName } : {}),
  };
};

export const getIsInFolder = (path: string) => {
  const splited = path.split('/').filter(Boolean);
  return splited.length > 1;
};

export const getFolderName = (path: string) =>
  path.split('/').filter(Boolean)[0];

export const createFileFormData = (fileName: string, file: File) => {
  const formData = new FormData();

  formData.append('fileName', fileName);
  formData.append('file', file);

  return formData;
};

const createFormDataWithFields = (
  file: File,
  fields: Record<string, string>,
) => {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append(
    'Content-Type',
    file.type || getMimeTypeByNameExtension(file.name),
  );
  formData.append('file', file);

  return formData;
};

export const uploadFileToUrl = async (
  presignedPost: PresignedFile,
  file: File,
  config?: AxiosRequestConfig,
) => {
  const { url, fields } = presignedPost;

  const formData = createFormDataWithFields(file, fields);

  await Axios.post(url, formData, config);
};

export const getUploadedFile = async (
  data: UploadedFileResponse,
  file: File,
  config?: AxiosRequestConfig,
) => {
  const { presignedPost, uploadedFile } = data;

  await uploadFileToUrl(presignedPost, file, config);

  return uploadedFile;
};

export const createDropzoneFile = (file: File) => {
  const { type, name, size } = file;

  return {
    ...file,
    name,
    size,
    type: type as FileType,
  };
};

export type UploadStructure = {
  files: EnhancedFile[];
  folders: Record<
    string,
    {
      files: EnhancedFile[];
    } & DuplicateNode
  >;
};

export const getUploadStructure = (acceptedFiles: EnhancedFile[]) => {
  const structure: UploadStructure = { files: [], folders: {} };

  acceptedFiles.forEach((file) => {
    const path = getFilePath(file);
    const isInFolder = getIsInFolder(path);

    if (!isInFolder) {
      structure.files.push(file);
      return;
    }

    const folderName = getFolderName(path);

    if (!structure.folders?.[folderName]) {
      structure.folders = {
        ...structure.folders,
        [folderName]: { files: [] },
      };
    }

    structure.folders[folderName].files.push(file);
  });

  return structure;
};

export const replaceFolderNameWithSuggested = (
  path: string,
  folderName: string,
  suggestedName: string,
) => path.replace(new RegExp(`^(/?)(${folderName})/`), `$1${suggestedName}/`);

export const cloneFile = (
  file: EnhancedFile,
  {
    name = file.name,
    path = getFilePath(file),
  }: { name?: string; path?: string },
): EnhancedFile =>
  Object.assign(
    new File([file], name, {
      type: file.type,
      lastModified: file.lastModified,
    }),
    { path },
  ) as EnhancedFile;
